import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, ArrowLeft, Trash2, Calendar, Clock, User as UserIcon, Dumbbell } from "lucide-react";
import DBWorkoutBuilder from "./workouts/DBWorkoutBuilder";
import { toast } from "sonner";
import { listTemplates, createTemplate, deleteTemplate, type WorkoutTemplateMeta } from "@/integrations/backend/workout-templates";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface WorkoutsViewProps {
  assignmentContext?: {
    clientId: string;
    assignmentDate: Date | null;
    backToClientId: string;
    assignedWorkoutId?: string;
  } | null;
  onClearAssignmentContext?: () => void;
}

const templatesListKey = queryKeys.workoutTemplates.list("All");

export default function WorkoutsView({ assignmentContext, onClearAssignmentContext }: WorkoutsViewProps = {}) {
  const queryClient = useQueryClient();
  const templatesQuery = useQuery({
    queryKey: templatesListKey,
    queryFn: () => listTemplates(),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...templatesListKey]),
  });
  const templates = templatesQuery.data ?? [];

  const [mode, setMode] = useState<"list" | "builder">("list");
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [workoutMode, setWorkoutMode] = useState<"template" | "assigned">("template");
  const [search, setSearch] = useState("");

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    // US date format, date only (no time)
    return format(d, "MMM d, yyyy");
  };

  useEffect(() => {
    if (templatesQuery.isError && templatesQuery.error) {
      toast.error("Could not load templates", { description: (templatesQuery.error as Error)?.message ?? String(templatesQuery.error) });
    }
  }, [templatesQuery.isError, templatesQuery.error]);

  const invalidateTemplates = () => queryClient.invalidateQueries({ queryKey: templatesListKey });

  const filtered = templates.filter((t) => (t.name || "").toLowerCase().includes(search.toLowerCase()));

  // Reusable function to duplicate a template
  const duplicateTemplate = async (sourceTemplateId: string): Promise<string> => {
    const { getTemplateMeta } = await import("@/integrations/backend/workout-templates");
    const { fetchTemplateGroupings } = await import("@/integrations/backend/workouts");
    const { addExerciseToTemplate } = await import("@/integrations/backend/workouts");
    const { updateGroupingSets } = await import("@/integrations/backend/workouts");
    const { updateExerciseFields } = await import("@/integrations/backend/workouts");
    
    const sourceMeta = await getTemplateMeta(sourceTemplateId);
    const sourceGroupings = await fetchTemplateGroupings(sourceTemplateId);
    
    // Create new template with source name + " copy"
    const templateId = await createTemplate(`${sourceMeta.name} copy`, sourceMeta.duration_minutes || 30);
    
    // Copy all groupings and exercises
    for (const grouping of sourceGroupings) {
      // Create grouping by adding first exercise (which creates grouping)
      if (grouping.exercises.length > 0) {
        const firstExercise = grouping.exercises[0];
        await addExerciseToTemplate(templateId, firstExercise.exercise);
        
        // Get the newly created grouping ID (we'll need to fetch to get it)
        const newGroupings = await fetchTemplateGroupings(templateId);
        const newGrouping = newGroupings[newGroupings.length - 1]; // Last one is the one we just created
        
        // Update grouping sets
        await updateGroupingSets(newGrouping.id, grouping.sets);
        
        // Add remaining exercises to this grouping
        for (let i = 1; i < grouping.exercises.length; i++) {
          const exercise = grouping.exercises[i];
          await addExerciseToTemplate(templateId, exercise.exercise);
          // Get updated groupings to find the exercise we just added
          const updatedGroupings = await fetchTemplateGroupings(templateId);
          const updatedGrouping = updatedGroupings.find(g => g.id === newGrouping.id);
          if (updatedGrouping) {
            const newExercise = updatedGrouping.exercises[updatedGrouping.exercises.length - 1];
            // Update exercise fields to match source
            await updateExerciseFields(newExercise.id, {
              sets: exercise.sets,
              reps: exercise.reps,
              time_seconds: exercise.time_seconds,
              rest_seconds: exercise.rest_seconds,
              weight_kg: exercise.weight_kg,
              weight_lbs: exercise.weight_lbs,
              difficulty: exercise.difficulty,
            });
          }
        }
        
        // Update first exercise fields
        const finalGroupings = await fetchTemplateGroupings(templateId);
        const finalGrouping = finalGroupings.find(g => g.id === newGrouping.id);
        if (finalGrouping && finalGrouping.exercises.length > 0) {
          const firstNewExercise = finalGrouping.exercises[0];
          await updateExerciseFields(firstNewExercise.id, {
            sets: firstExercise.sets,
            reps: firstExercise.reps,
            time_seconds: firstExercise.time_seconds,
            rest_seconds: firstExercise.rest_seconds,
            weight_kg: firstExercise.weight_kg,
            weight_lbs: firstExercise.weight_lbs,
            difficulty: firstExercise.difficulty,
          });
        }
      }
    }
    
    await invalidateTemplates();
    return templateId;
  };

  const [editingAssignedWorkoutId, setEditingAssignedWorkoutId] = useState<string | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      await deleteTemplate(templateToDelete);
      toast.success("Workout template deleted");
      setTemplateToDelete(null);
      await invalidateTemplates();
    } catch (error: any) {
      toast.error("Could not delete template", { description: error?.message ?? String(error) });
    }
  };

  // Auto-open builder if assignment context is provided
  useEffect(() => {
    if (assignmentContext && !editingAssignedWorkoutId && !editingTemplateId && mode !== "builder") {
      // If we have an assigned workout ID, open it in assigned mode
      if (assignmentContext.assignedWorkoutId) {
        setEditingAssignedWorkoutId(assignmentContext.assignedWorkoutId);
        setWorkoutMode("assigned");
        setMode("builder");
      }
      // Note: If assignmentContext exists but assignedWorkoutId is missing,
      // it means the workout hasn't been created yet. This shouldn't happen
      // in normal flow since AssignWorkoutDialog always creates the workout first.
      // In that case, we'll stay on the list view.
    }
  }, [assignmentContext, editingAssignedWorkoutId, editingTemplateId, mode]);

  if (mode === "builder" && (editingTemplateId || editingAssignedWorkoutId)) {
    return (
      <div className="h-full flex flex-col gap-4">
        <div className="flex-1 min-h-0">
          <DBWorkoutBuilder
            templateId={editingTemplateId || undefined}
            assignedWorkoutId={editingAssignedWorkoutId || undefined}
            onClose={() => { 
              if (assignmentContext && onClearAssignmentContext) {
                // Navigate back to client page
                onClearAssignmentContext();
              } else {
                // Navigate back to templates list
                setMode("list"); 
                setEditingTemplateId(null); 
                setEditingAssignedWorkoutId(null);
                void invalidateTemplates();
              }
            }}
            workoutMode={editingAssignedWorkoutId ? "assigned" : workoutMode}
            clientId={assignmentContext?.clientId || null}
            assignmentDate={assignmentContext?.assignmentDate || null}
            backContext={assignmentContext ? "client" : "templates"}
            backToClientId={assignmentContext?.backToClientId}
            onEditTemplate={(templateId) => {
              setEditingAssignedWorkoutId(null);
              setEditingTemplateId(templateId);
              setWorkoutMode("template");
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Workout Templates</h1>
          <p className="text-gray-600">Create and manage reusable workouts.</p>
        </div>
        <Button onClick={async () => {
          try {
            const templateId = await createTemplate("Untitled Workout", 30);
            await invalidateTemplates();
            setWorkoutMode("template");
            setEditingTemplateId(templateId);
            setMode("builder");
          } catch (error: any) {
            toast.error("Could not create workout", { description: error?.message ?? String(error) });
          }
        }}>New Workout</Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input placeholder="Search templates..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <div />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((t) => (
          <Card
            key={t.id}
            className="group overflow-hidden border border-gray-200/70 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl"
          >
            <div className="h-1 w-full bg-gradient-to-r from-primary/70 via-primary to-primary/40" />
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-lg font-semibold text-gray-900 line-clamp-2">{t.name}</CardTitle>

                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1">
                      <Clock className="h-3.5 w-3.5" />
                      {t.duration_minutes ?? 0} min
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1">
                      <Dumbbell className="h-3.5 w-3.5" />
                      {typeof t.exercise_count === "number" ? t.exercise_count : "—"} exercises
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-3 grid gap-2 text-xs text-gray-600">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Created {formatDate(t.created_at)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Saved {formatDate(t.updated_at)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="inline-flex items-center gap-1">
                    <UserIcon className="h-3.5 w-3.5" />
                    {t.creator_email || "—"}
                  </span>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              {t.difficulty_counts ? (
                <div className="mb-4 flex flex-wrap gap-2">
                  {(["Beginner", "Intermediate", "Expert", "Pro"] as const)
                    .map((lvl) => ({ lvl, n: t.difficulty_counts?.[lvl] ?? 0 }))
                    .filter(({ n }) => (n ?? 0) > 0)
                    .map(({ lvl, n }) => (
                      <span
                        key={lvl}
                        className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700"
                      >
                        {lvl}: {n}
                      </span>
                    ))}
                </div>
              ) : null}

              {t.primary_muscles_top && t.primary_muscles_top.length > 0 ? (
                <div className="mb-4 flex flex-wrap gap-2">
                  {t.primary_muscles_top.slice(0, 5).map((m) => (
                    <span
                      key={m}
                      className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              ) : null}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setWorkoutMode("template");
                    setEditingTemplateId(t.id);
                    setMode("builder");
                  }}
                >
                  Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setTemplateToDelete(t.id)} aria-label="Delete workout">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div />

      <AlertDialog open={Boolean(templateToDelete)} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialogContent className="bg-white text-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Delete Workout Template?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              This action cannot be undone. The workout template will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-gray-900 border-gray-300 hover:bg-gray-50">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTemplate} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}






